import React from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  pdf,
} from "@react-pdf/renderer";
import { Message } from "@lango/api/src/types";
import { format } from "date-fns";

// Chat bubble colors
const userBubbleColor = "#C9F4E7"; // primary-color-light
const botBubbleColor = "#eef3f5bd";

// Styles
const styles = StyleSheet.create({
  page: {
    padding: 15,
    backgroundColor: "#ffffff",
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: 6,
    color: "#000000",
  },
  date: {
    fontSize: 10,
    textAlign: "center",
    marginBottom: 20,
    color: "#808080",
  },
  messageContainer: {
    marginBottom: 6,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  userMessageContainer: {
    justifyContent: "flex-end",
  },
  botMessageContainer: {
    justifyContent: "flex-start",
  },
  bubble: {
    maxWidth: "75%",
    paddingTop: 7,
    paddingBottom: 7,
    paddingLeft: 10,
    paddingRight: 10,
    borderRadius: 12,
    backgroundColor: userBubbleColor,
    alignSelf: "flex-start",
  },
  botBubble: {
    backgroundColor: botBubbleColor,
  },
  bubbleText: {
    fontSize: 15,
    color: "#373941",
    lineHeight: 1.3,
    textAlign: "left",
  },
  translationDivider: {
    borderTopWidth: 0.3,
    borderTopColor: "rgba(0, 0, 0, 0.1)",
    marginTop: 3,
    marginBottom: 3,
  },
  translationLabel: {
    fontSize: 10,
    color: "rgba(55, 57, 65, 0.6)",
    marginBottom: 2,
  },
  translationText: {
    fontSize: 15,
    color: "#373941",
    lineHeight: 1.3,
    paddingLeft: 2,
    textAlign: "left",
  },
  timestamp: {
    fontSize: 8,
    color: "#808080",
    marginHorizontal: 5,
    alignSelf: "flex-start",
    paddingTop: 7,
  },
});

// PDF Document Component
const ChatPDFDocument: React.FC<{
  messages: Message[];
  botName?: string;
  translations?: Record<string, string>;
}> = ({ messages, botName, translations }) => {
  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Text style={styles.title}>
          Conversation with {botName || "AI Chat"}
        </Text>
        <Text style={styles.date}>
          Generated on: {format(new Date(), "PPpp")}
        </Text>

        {messages.map((message) => {
          const isUser = message.sender === "user";
          const text = message.text || "";
          const translation = !isUser && translations?.[message.id];

          return (
            <View
              key={message.id}
              style={[
                styles.messageContainer,
                isUser
                  ? styles.userMessageContainer
                  : styles.botMessageContainer,
              ]}
            >
              {!isUser && message.timestamp && (
                <Text style={styles.timestamp}>
                  {format(new Date(message.timestamp), "HH:mm")}
                </Text>
              )}
              <View style={[styles.bubble, !isUser && styles.botBubble]}>
                <Text style={styles.bubbleText} wrap>
                  {text}
                </Text>
                {translation && (
                  <>
                    <View style={styles.translationDivider} />
                    <Text style={styles.translationLabel}>
                      Traduction (par Google):
                    </Text>
                    <Text style={styles.translationText} wrap>
                      {translation}
                    </Text>
                  </>
                )}
              </View>
              {isUser && message.timestamp && (
                <Text style={styles.timestamp}>
                  {format(new Date(message.timestamp), "HH:mm")}
                </Text>
              )}
            </View>
          );
        })}
      </Page>
    </Document>
  );
};

// Download function
export const downloadChatAsPDF = async (
  messages: Message[],
  botName?: string,
  translations?: Record<string, string>
): Promise<void> => {
  if (!messages || messages.length === 0) {
    return;
  }

  try {
    const blob = await pdf(
      <ChatPDFDocument
        messages={messages}
        botName={botName}
        translations={translations}
      />
    ).toBlob();

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const safeBotName = (botName || "chat")
      .replace(/[^a-z0-9]/gi, "-")
      .toLowerCase();
    const fileName = `chat-${safeBotName}-${format(
      new Date(),
      "yyyy-MM-dd"
    )}.pdf`;

    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error generating PDF:", error);
  }
};
